import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLazyQuery, useQuery } from '@apollo/client/react';
import cn from 'clsx';
import style from './style.module.scss';
import { BasicDropDownController } from '@components-clean/core/basic-dropdown';
import { DatePickerController } from '@components-clean/core/date-picker';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { TextAreaController } from '@components-clean/core/text-area';
import { TextAreaDropdownController } from '@components-clean/core/text-area-dropdown';
import { formatDatasetDates, sortDatasetByDatesDesc } from '@components-clean/navbar/dataset-selector.utils';
import { useUnsavedChangesConfirmation, useUnsavedChangesTracking } from '@hooks-clean/index';
import {
    ObservationInputs,
    ObservationDialogProps,
} from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/observation.interface';
import { useTranslation } from '@hooks/index';
import { stringDateWithTimeFromUtcNonNullable } from '@utils/convertor';
import { GET_CONTROLS_HUB_CONFIG_PANELS_RESOLVED } from '@queries/central-monitoring';
import {
    getControlsHubConfigPanelsForStudyResolved as getCm2PanelsResponse,
    getControlsHubConfigPanelsForStudyResolvedVariables,
} from '@queries/generated/central-monitoring.generated';
import {
    getStudyMembers,
    getStudyMembers_OpraQueries_studyMembers_StudyMember,
    getStudyMembersVariables,
} from '@queries/generated/studies.generated';
import { GET_STUDY_MEMBERS } from '@queries/studies';
import ControlsHubApi from '@apis/bff-api/central-monitoring/controls-hub';
import DatasetApi from '@apis/bff-api/central-monitoring/dataset';

interface Panel {
    reference: string;
    plotTitle: string;
}

const COMPONENT_NAME = 'add-controls-observation-dialog';

const AddControlsObservationDialog = ({ onClose, onSubmit, studyRef, observation }: ObservationDialogProps) => {
    const { t } = useTranslation('cm');

    // State
    const [studyMembers, setStudyMembers] = useState<
        ({ fullName: string; ref: string; groupedBy: string } | null)[] | undefined
    >([]);

    const [panelItems, setPanelItems] = useState<Panel[]>();
    const [timePeriods, setTimePeriods] = useState<string[]>([]);

    // Load Datasets
    const { data: datasetOpenedOn, isLoading: isLoadingDatasets } = DatasetApi.useGetAll(studyRef);
    const cm2DataSets = datasetOpenedOn?.filter((x) => x.active === true)?.filter(Boolean) ?? [];
    const sortedCM2DataSets = cm2DataSets.map(formatDatasetDates).sort(sortDatasetByDatesDesc) ?? [];
    // Load all panels for a dataset with latest titles
    const [loadPanels, { data: getPanelsRawData, loading: loadingPanels }] = useLazyQuery<
        getCm2PanelsResponse,
        getControlsHubConfigPanelsForStudyResolvedVariables
    >(GET_CONTROLS_HUB_CONFIG_PANELS_RESOLVED);

    // Load Study Members
    const { data: studyMembersData, loading: loadingStudyMembers } = useQuery<
        getStudyMembers,
        getStudyMembersVariables
    >(GET_STUDY_MEMBERS, {
        variables: { studyId: studyRef },
    });

    useEffect(() => {
        if (studyMembersData?.studyMembers) {
            const studyUsers = studyMembersData.studyMembers
                ?.filter((user): user is getStudyMembers_OpraQueries_studyMembers_StudyMember => !!user)
                .map((user) => ({
                    fullName: `${user.firstName} ${user.lastName}`,
                    ref: user.id as string,
                    groupedBy: user.active ? 'Active' : 'Inactive',
                    disabled: !user.active,
                }))
                .sort((a, b) => {
                    if (a.disabled !== b.disabled) {
                        return a.disabled ? 1 : -1;
                    }
                    return a.fullName.localeCompare(b.fullName);
                });
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/set-state-in-effect
            setStudyMembers(studyUsers);
        }
    }, [studyMembersData?.studyMembers]);

    const {
        control,
        handleSubmit,
        watch,
        resetField,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm<ObservationInputs>({
        mode: 'all',
        defaultValues: observation || {
            datasetOpenedOn: null,
            // No data view for association
            associationType: null,
            panel: null,
            site: null,
            owner: null,
            observationDate: null,
            observation: '',
            rootCause: '',
            comments: '',
            closedDate: null,
            // All Controls Hub panels are scatter plot models
            isScatterPlot: true,
            timePeriod: null,
        },
    });
    const [watchDatasetOpenedOn, watchPanel] = watch(['datasetOpenedOn', 'panel']);

    const { data: timePeriodData, isLoading: timePeriodLoading } = ControlsHubApi.useGetTimePeriodsForPanel(
        studyRef,
        watchDatasetOpenedOn?.id,
        watchPanel?.reference,
    );

    useEffect(() => {
        // A tad annoying, the date is a string but formatted as an iso, but we want a different format in the dropdown
        // Could potentially update the API to return a formatted string to save the additional parsing on the FE.
        // To add to the annoyance on save we then need to parse the formatted string back to an ISO string
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setTimePeriods(
            timePeriodData?.map((d) => {
                return stringDateWithTimeFromUtcNonNullable(d, 'DD-MMM-yyyy')!;
            }) ?? [],
        );
    }, [timePeriodData]);

    useUnsavedChangesTracking({
        area: 'central-monitoring-toolkit',
        componentName: COMPONENT_NAME,
        hasChanges: isDirty,
    });
    const confirmLossOfChanges = useUnsavedChangesConfirmation({
        area: 'central-monitoring-toolkit',
        mode: 'individual-components',
        componentNames: COMPONENT_NAME,
    });

    useEffect(() => {
        if (getPanelsRawData?.getControlsHubConfigPanelsResolvedForStudy?.panels) {
            const panels = getPanelsRawData?.getControlsHubConfigPanelsResolvedForStudy.panels
                ?.filter((item) => item != null && item.active)
                .map((item) => ({
                    reference: item?.panelRef as string,
                    plotTitle: item?.plotTitle ?? '[Title Missing]',
                }))
                .sort((a, b) => a.plotTitle.localeCompare(b.plotTitle));
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/set-state-in-effect
            setPanelItems(panels);
            resetField('panel');
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [getPanelsRawData]);

    useEffect(() => {
        resetField('panel');
        resetField('site');

        if (watchDatasetOpenedOn) {
            loadPanels({
                variables: { studyId: studyRef, timestamp: watchDatasetOpenedOn?.created },
            }).catch((err) => window.console.error(err));
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [watchDatasetOpenedOn]);

    const dialogTitle = `${t('common:add')} ${t('observationDialog.controlsTitle')}`;
    const primaryBtnLabel = t('common:add');

    const onCloseHandler = async () => {
        const confirmed = await confirmLossOfChanges();
        if (!confirmed) {
            return;
        }

        onClose();
    };

    return (
        <DraggableDialog
            className={style.kWindowContent}
            dataTestId="add-observation-dialog"
            title={dialogTitle}
            onClose={onCloseHandler}
            onSubmit={() => handleSubmit(onSubmit)()}
            primaryBtnLabel={primaryBtnLabel}
            primaryBtnDisabled={!isValid || !isDirty || isSubmitting}
            secondaryBtnDisabled={isSubmitting}
            initialHeight={630}
            initialWidth={522}
        >
            <form id="add-edit-observation" className="k-w-full k-h-full">
                <div className={cn('k-shrink-0 k-d-flex k-justify-content-center k-pb-md', style.formFieldGap)}>
                    <div className="!k-basis-1/2">
                        <BasicDropDownController
                            control={control}
                            name="datasetOpenedOn"
                            data-testid="field-select-datasetOpenedOn"
                            data={sortedCM2DataSets}
                            required
                            textField="dataCutDate"
                            dataItemKey="id"
                            label={t('observationDialog.datasetOpenedOn')}
                            loading={isLoadingDatasets}
                            disabled={isLoadingDatasets}
                            filterable
                            allowClear
                            readOnly={isSubmitting}
                        />
                    </div>

                    <div className="!k-basis-1/2">
                        <div className="!k-basis-1/2">
                            <DatePickerController
                                control={control}
                                name="observationDate"
                                data-testid="datepicker-observationDate"
                                label={t('observationDialog.observationDate')}
                                fillMode="solid"
                                required
                                readonly={isSubmitting}
                            />
                        </div>
                    </div>
                </div>
                <div className="k-shrink-0 k-d-flex k-justify-content-center k-pb-md">
                    <BasicDropDownController
                        control={control}
                        name="panel"
                        data-testid="field-select-panel"
                        data={panelItems ?? []}
                        required
                        textField="plotTitle"
                        disabled={!watchDatasetOpenedOn || loadingPanels}
                        label={`${t('observationDialog.panel')} *`}
                        loading={loadingPanels}
                        filterable
                        allowClear
                        dataItemKey="reference"
                        handleInputChange={() => resetField('timePeriod')}
                        readOnly={isSubmitting}
                    />
                </div>

                <div className="k-shrink-0 k-d-flex k-justify-content-center k-pb-md">
                    <BasicDropDownController
                        control={control}
                        name="timePeriod"
                        data-testid="field-time-period"
                        data={timePeriods}
                        loading={timePeriodLoading}
                        disabled={watchPanel?.reference == null}
                        label={t('observationDialog.timePeriod')}
                        filterable
                        allowClear
                        required
                        readOnly={isSubmitting}
                    />
                </div>

                <div className="k-shrink-0 k-d-flex k-justify-content-center k-pb-md">
                    <BasicDropDownController
                        control={control}
                        name="owner"
                        data-testid="field-select-owner"
                        data={studyMembers ?? []}
                        textField="fullName"
                        groupField="groupedBy"
                        dataItemKey="ref"
                        label={t('observationDialog.owner')}
                        loading={loadingStudyMembers}
                        disabled={loadingStudyMembers}
                        filterable
                        allowClear
                        readOnly={isSubmitting}
                    />
                </div>
                <div className="k-d-flex k-flex-auto k-justify-content-center k-pb-md">
                    <TextAreaController
                        className={style.textAreaOverrides}
                        maxLength={3000}
                        control={control}
                        name="observation"
                        required
                        data-testid="field-select-observation"
                        label={t('observationDialog.observation')}
                        readOnly={isSubmitting}
                    />
                </div>
                <div className="k-pb-md k-input-md">
                    <TextAreaDropdownController
                        maxLength={3000}
                        className={style.textAreaOverrides}
                        control={control}
                        name="rootCause"
                        data-testid="field-select-rootCause"
                        label={t('observationDialog.rootCause')}
                        rows={4}
                        readOnly={isSubmitting}
                    />
                </div>
                <div className="k-pb-md k-input-md">
                    <TextAreaDropdownController
                        maxLength={3000}
                        control={control}
                        className={style.textAreaOverrides}
                        name="comments"
                        data-testid="field-select-comments"
                        label={t('observationDialog.comments')}
                        rows={4}
                        readOnly={isSubmitting}
                    />
                </div>
                <div className="k-shrink-0 k-d-flex k-justify-content-start k-pb-md">
                    <div className="k-flex-row !k-basis-1/2">
                        <DatePickerController
                            control={control}
                            name="closedDate"
                            data-testid="datepicker-closedDate"
                            label={t('observationDialog.closedDate')}
                            fillMode="solid"
                            readonly={isSubmitting}
                        />
                    </div>
                </div>
            </form>
        </DraggableDialog>
    );
};

export default AddControlsObservationDialog;
